import React, { useMemo, useState } from 'react'
import styles from './Pricing.module.css';
import Navbar from '../../Components/Navbar/Navbar';
import Footer from '../../Components/Footer/Footer';
import ProjectServices from '../../services/projectServices';
import { InputNumber, Modal, message } from 'antd';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getTranslation } from '../../util/utilFunc';

const pricingPlans = [
    {
        name: "Freemium Plan",
        price: "£0",
        period: "/month",
        popular: false,
        description: "Perfect for testing out the platform and small teams getting started",
        features: [
            { label: "For", value: "Individuals and small teams exploring Compelion" },
            { label: "Users", value: "1 user + 1 collaborator" },
            { label: "Projects", value: "Up to 6 active projects" },
            { label: "AiFeatures", value: "Basic AI feature, with limited capabilities." },
            { label: "Integrations", value: "No Integrations" },
            { label: "Support", value: "Email Support only" },
            { label: "Customisation", value: "Customisation Not supported" },
        ],
        buttonText: "Get Started Free",
    },
    {
        name: "Team Plan",
        price: "£15",
        period: "/user/month",
        popular: false,
        description: "Most popular for growing teams and organizations",
        features: [
            { label: "For", value: "Small to medium-sized teams" },
            { label: "Users", value: "Up to 100 users" },
            { label: "Projects", value: "Up to 20 active projects" },
            { label: "AiFeatures", value: "All AI tools with increased limits" },
            { label: "Integrations", value: "Seamless with Google Workspace and Microsoft 365" },
            { label: "Support", value: "Priority email and chat support" },
            { label: "Customisation", value: "Limited Customisation" },
        ],
        buttonText: "Start Free Trial",
    },
    {
        name: "Growth Plan",
        price: "£20",
        period: "/user/month",
        popular: true,
        description: "Ideal for scaling organizations with advanced needs",
        features: [
            { label: "For", value: "Growing organisations" },
            { label: "Users", value: "Up to 500 users" },
            { label: "Projects", value: "Up to 50 active projects" },
            { label: "AiFeatures", value: "AI with increased capabilities" },
            { label: "Integrations", value: "Enhanced tools for Google Workspace and Microsoft 365" },
            { label: "Support", value: "Dedicated success support manager" },
            { label: "Customisation", value: "Limited Customisation" },
        ],
        buttonText: "Start Free Trial",
    },
    {
        name: "Enterprise Plan",
        price: "Custom",
        period: "",
        popular: false,
        description: "Enterprise-grade solution with custom features",
        features: [
            { label: "For", value: "Large enterprises" },
            { label: "Users", value: "Unlimited Users" },
            { label: "Projects", value: "Unlimited Projects" },
            { label: "AiFeatures", value: "Tailored AI solutions with deep analytics" },
            { label: "Integrations", value: "Enterprise-grade Google Workspace and Microsoft 365" },
            { label: "Support", value: "24/7 with a dedicated account manager" },
            { label: "Customisation", value: "Full workflow and system integration" },
        ],
        buttonText: "Contact Sales",
    },
]

// Feature comparison data for detailed table
const featureCategories = [
    {
        category: "Core Features",
        features: [
            {
                name: "Users Included",
                freemium: "1 + 1 collaborator",
                team: "Up to 100 users",
                growth: "Up to 500 users",
                enterprise: "Unlimited"
            },
            {
                name: "Projects",
                freemium: "Up to 6 active projects",
                team: "Up to 20 active projects",
                growth: "Up to 50 active projects",
                enterprise: "Unlimited"
            },
            {
                name: "Storage",
                freemium: "1 GB",
                team: "10 GB per user",
                growth: "50 GB per user",
                enterprise: "Unlimited"
            },
            {
                name: "File Upload Size",
                freemium: "10 MB",
                team: "100 MB",
                growth: "1 GB",
                enterprise: "Unlimited"
            }
        ]
    },
    {
        category: "AI & Analytics",
        features: [
            {
                name: "Zoro ( Idea Management AI )",
                freemium: true,
                team: true,
                growth: true,
                enterprise: true
            },
            {
                name: "Fulcrum ( Project Management AI )",
                freemium: true,
                team: true,
                growth: true,
                enterprise: true
            },
            {
                name: "Axis ( Task Assistant AI )",
                freemium: '10 generations/month',
                team: '100 generations/month',
                growth: '250 generations/month',
                enterprise: 'Unlimited'
            },
            {
                name: "Prism ( Problem-Solving AI )",
                freemium: '2 idea creations per type/month',
                team: '10 idea creations per type/month',
                growth: '25 idea creations per type/month',
                enterprise: 'Unlimited'
            },
            {
                name: "Milestone Generation",
                freemium: '10 generations/month',
                team: '100 generations/month',
                growth: '250 generations/month',
                enterprise: 'Unlimited'
            },
            {
                name: "Advanced Analytics & Reporting",
                freemium: true,
                team: true,
                growth: true,
                enterprise: true
            }
        ]
    },
    {
        category: "Collaboration",
        features: [
            {
                name: "Real-time Collaboration",
                freemium: true,
                team: true,
                growth: true,
                enterprise: true
            },
            {
                name: "Comments & Mentions",
                freemium: "Basic",
                team: "Advanced",
                growth: "Advanced",
                enterprise: "Advanced"
            },
            {
                name: "Team Permissions",
                freemium: "Basic",
                team: "Role-based",
                growth: "Advanced RBAC",
                enterprise: "Custom Permissions"
            },
        ]
    },
    {
        category: "Integrations",
        features: [
            {
                name: "Google Workspace",
                freemium: false,
                team: true,
                growth: true,
                enterprise: true
            },
            {
                name: "Microsoft 365",
                freemium: false,
                team: true,
                growth: true,
                enterprise: true
            },
            {
                name: "API Access",
                freemium: false,
                team: "Limited",
                growth: "Limited",
                enterprise: "Custom APIs"
            },
            {
                name: "Webhooks",
                freemium: false,
                team: false,
                growth: false,
                enterprise: true
            }
        ]
    },
    {
        category: "Security & Compliance",
        features: [
            {
                name: "SSL Encryption",
                freemium: true,
                team: true,
                growth: true,
                enterprise: true
            },
            {
                name: "Secure Authentication",
                freemium: true,
                team: true,
                growth: true,
                enterprise: true
            },
            {
                name: "SOC 2 Compliance",
                freemium: true,
                team: true,
                growth: true,
                enterprise: true
            },
            { 
                name: "Single Sign-On (SSO)",
                freemium: true,
                team: true,
                growth: true,
                enterprise: true
            },
            {
                name: "ISO 27001 Certification",
                freemium: true,
                team: true,
                growth: true,
                enterprise: true
            }
        ]
    },
    {
        category: "Support",
        features: [
            {
                name: "Email Support",
                freemium: "Standard",
                team: "Priority",
                growth: "Priority",
                enterprise: "24/7 Priority"
            },
            {
                name: "Chat Support",
                freemium: false,
                team: true,
                growth: true,
                enterprise: true
            },
            {
                name: "Phone Support",
                freemium: false,
                team: false,
                growth: true,
                enterprise: true
            },
            {
                name: "Dedicated Success Manager",
                freemium: false,
                team: false,
                growth: true,
                enterprise: true
            }
        ]
    }
];

// FAQ data
const faqData = [
    {
        question: "Can I change my plan at any time?",
        answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and you'll be charged or credited on a pro-rated basis."
    },
    {
        question: "Is there a free trial available?",
        answer: "We offer freemium plan with limited features. You can explore the platform and upgrade to a paid plan whenever you're ready."
    },
    {
        question: "What happens to my data if I cancel?",
        answer: "Your data remains accessible for 30 days after cancellation. You can export your data at any time during this period. Contact support for assistance."
    },
    {
        question: "Do you offer discounts for annual plans?",
        answer: "Yes, we offer a 20% discount when you pay annually. Contact our sales team for custom enterprise pricing."
    },
    {
        question: "Is my data secure?",
        answer: "Absolutely. We use enterprise-grade security with SSL encryption, regular backups, and SOC 2 compliance for all plans."
    },
    {
        question: "Can I add more users to my plan?",
        answer: "Yes, you can add or remove users at any time. You'll be charged pro-rated for additional users."
    }
];

const pricingPlansArabic = [
    {
        name: "الخطة المجانية",
        price: "£0",
        period: "/شهر",
        popular: false,
        description: "اكتشف قوة Compelion مجانًا - مثالي للأفراد والفرق الصغيرة التي تبدأ رحلتها في الإدارة الفعالة للمشاريع",
        features: [
            { label: "مناسبة لـ", value: "الأفراد والفرق الصغيرة التي تستكشف Compelion" },
            { label: "المستخدمون", value: "مستخدم واحد + متعاون واحد" },
            { label: "المشاريع", value: "إنشاء وإدارة مشاريع محدودة" },
            { label: "ميزات الذكاء الاصطناعي", value: "ميزات أساسية للذكاء الاصطناعي مع قدرات محدودة" },
            { label: "التكاملات", value: "لا توجد" },
            { label: "الدعم", value: "البريد الإلكتروني فقط" },
            { label: "التخصيص", value: "غير مدعوم حاليًا" },
        ],
        buttonText: "ابدأ مجانًا",
    },
    {
        name: "خطة الفريق",
        price: "£15",
        period: "/مستخدم/شهر",
        description: "الخيار الأمثل للفرق الديناميكية - استمتع بأدوات متقدمة تدفع نموك إلى الأمام",
        features: [
            { label: "مناسبة لـ", value: "الفرق الصغيرة إلى المتوسطة" },
            { label: "المستخدمون", value: "حتى 100 مستخدم" },
            { label: "المشاريع", value: "زيادة في إنشاء وإدارة المشاريع" },
            { label: "ميزات الذكاء الاصطناعي", value: "جميع أدوات الذكاء الاصطناعي مع حدود أعلى" },
            { label: "التكاملات", value: "تكامل سلس مع Google Workspace وMicrosoft 365" },
            { label: "الدعم", value: "أولوية البريد الإلكتروني والدردشة" },
            { label: "التخصيص", value: "محدود" },
        ],
        buttonText: "ابدأ النسخة التجريبية المجانية",
        popular: false,
    },
    {
        name: "خطة النمو",
        price: "£20",
        period: "/مستخدم/شهر",
        popular: true,
        description: "قم بتوسيع إمكانياتك مع حلول متقدمة مصممة للمنظمات الطموحة",
        features: [
            { label: "مناسبة لـ", value: "المنظمات النامية" },
            { label: "المستخدمون", value: "حتى 500 مستخدم" },
            { label: "المشاريع", value: "زيادة في إنشاء وإدارة المشاريع" },
            { label: "ميزات الذكاء الاصطناعي", value: "ذكاء اصطناعي مع قدرات أعلى" },
            { label: "التكاملات", value: "أدوات معززة لـ Google Workspace وMicrosoft 365" },
            { label: "الدعم", value: "مدير نجاح مخصص" },
            { label: "التخصيص", value: "محدود" },
        ],
        buttonText: "ابدأ النسخة التجريبية المجانية",
    },
    {
        name: "خطة المؤسسة",
        price: "مخصص",
        period: "",
        popular: false,
        description: "حلول مخصصة للمؤسسات العملاقة - أداء استثنائي وتكامل كامل",
        features: [
            { label: "مناسبة لـ", value: "المؤسسات الكبيرة" },
            { label: "المستخدمون", value: "غير محدود" },
            { label: "المشاريع", value: "غير محدودة" },
            { label: "ميزات الذكاء الاصطناعي", value: "حلول ذكاء اصطناعي مخصصة مع تحليلات عميقة" },
            { label: "التكاملات", value: "تكامل على مستوى المؤسسات مع Google Workspace وMicrosoft 365" },
            { label: "الدعم", value: "24/7 مع مدير حساب مخصص" },
            { label: "التخصيص", value: "تخصيص كامل لسير العمل وتكامل الأنظمة" },
        ],
        buttonText: "اتصل بالمبيعات",
    },
]

// Onboarding confidence steps (EN & AR text inside objects)
const onboardingSteps = [
    {
        step: 1,
        title_en: "Create your workspace",
        title_ar: "أنشئ مساحة العمل الخاصة بك",
        text_en: "Spin up a secure workspace in seconds. No credit card needed to start.",
        text_ar: "أنشئ مساحة عمل آمنة خلال ثوانٍ — بدون بطاقة ائتمان للبدء.",
    },
    {
        step: 2,
        title_en: "Import & organise",
        title_ar: "استيراد وتنظيم",
        text_en: "Bring existing ideas, tasks and documents. Smart mapping keeps structure intact.",
        text_ar: "اجلب الأفكار والمهام والملفات الحالية — نقوم بمواءمتها بذكاء دون فقد السياق.",
    },
    {
        step: 3,
        title_en: "Invite your team",
        title_ar: "ادعُ فريقك",
        text_en: "Role-based access lets you onboard safely while keeping control.",
        text_ar: "التحكم القائم على الأدوار يمنحك أمانًا كاملاً أثناء إضافة المستخدمين.",
    },
    {
        step: 4,
        title_en: "Automate and scale",
        title_ar: "أتمت ووسّع",
        text_en: "Add integrations & automations as you grow—no refactor required.",
        text_ar: "أضف التكاملات والأتمتة مع نموك—دون إعادة بناء أو تعقيد.",
    },
];

const onboardingMeta = {
    title_en: "Effortless onboarding. Enterprise-ready from day one.",
    title_ar: "تجربة انضمام سلسة — جاهزة للمؤسسات من اليوم الأول",
    subtitle_en: "Whether you're a founder or an IT lead, Compelion guides you from first login to measurable outcomes in minutes.",
    subtitle_ar: "سواء كنت مؤسسًا أو مسؤول تقنية، نأخذك من أول تسجيل دخول إلى نتائج ملموسة خلال دقائق.",
    note_en: "All plans include guided setup, contextual tips and optional human support to accelerate adoption.",
    note_ar: "جميع الخطط تتضمن إعدادًا موجهًا ونصائح سياقية ودعمًا بشريًا اختياريًا لتسريع التبني.",
};



// FAQ Component
const FAQItem = ({ question, answer, isOpen, onClick }) => {
    return (
        <div className={styles.faqItem}>
            <button className={styles.faqQuestion} onClick={onClick}>
                <span>{question}</span>
                <span className={`${styles.faqIcon} ${isOpen ? styles.faqIconOpen : ''}`}>
                    +
                </span>
            </button>
            {isOpen && (
                <div className={styles.faqAnswer}>
                    <p>{answer}</p>
                </div>
            )}
        </div>
    );
};

// Feature Comparison Table Component
const FeatureComparisonTable = () => {
    const { language } = useSelector(state => state.auth);
    const [openCategories, setOpenCategories] = useState(featureCategories.map(() => false)); // Start all closed

    const toggleCategory = (index) => {
        setOpenCategories(prev => prev.map((open, i) => i === index ? !open : open));
    };

    const renderFeatureValue = (value) => {
        if (typeof value === 'boolean') {
            return value ? (
                <span className={styles.checkmark}>✓</span>
            ) : (
                <span className={styles.cross}>✗</span>
            );
        }

        // If the value is a string, try to translate it so table cells switch to Arabic
        if (typeof value === 'string') {
            return <span className={styles.featureText}>{getTranslation(value, language)}</span>;
        }

        // Fallback to default rendering for other types
        return <span className={styles.featureText}>{value}</span>;
    };

    return (
        <div className={styles.featureTable}>
            <h2 className={styles.tableTitle}>{getTranslation("Detailed Feature Comparison", language)}</h2>
            <div className={styles.tableWrapper}>
                <table className={styles.comparisonTable}>
                    <thead>
                        <tr>
                            <th>{getTranslation("Features", language)}</th>
                            <th>{getTranslation("Freemium", language)}</th>
                            <th>{getTranslation("Team", language)}</th>
                            <th>{getTranslation("Growth", language)}</th>
                            <th>{getTranslation("Enterprise", language)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {featureCategories.map((category, categoryIndex) => (
                            <React.Fragment key={categoryIndex}>
                                <tr className={styles.categoryRow}>
                                    <td colSpan="5" className={styles.categoryHeader}>
                                        <button 
                                            className={styles.categoryButton} 
                                            onClick={() => toggleCategory(categoryIndex)}
                                        >
                                            <span>{getTranslation(category.category, language)}</span>
                                            <span className={`${styles.categoryIcon} ${openCategories[categoryIndex] ? styles.categoryIconOpen : ''}`}>
                                                +
                                            </span>
                                        </button>
                                    </td>
                                </tr>
                                {openCategories[categoryIndex] && category.features.map((feature, featureIndex) => (
                                    <tr key={featureIndex} className={styles.featureRow}>
                                        <td className={styles.featureName}>{getTranslation(feature.name, language)}</td>
                                        <td className={styles.featureValue}>
                                            {renderFeatureValue(feature.freemium)}
                                        </td>
                                        <td className={styles.featureValue}>
                                            {renderFeatureValue(feature.team)}
                                        </td>
                                        <td className={styles.featureValue}>
                                            {renderFeatureValue(feature.growth)}
                                        </td>
                                        <td className={styles.featureValue}>
                                            {renderFeatureValue(feature.enterprise)}
                                        </td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Trust Indicators Component
const TrustIndicators = () => {
    const { language } = useSelector(state => state.auth);

    return (
        <div className={styles.trustSection}>
            <h2 className={styles.trustTitle}>{getTranslation("Trusted by thousands of teams worldwide", language)}</h2>
            <div className={styles.trustBadges}>
                <div className={styles.trustBadge}>
                    <div className={styles.badgeIcon}>🔒</div>
                    <h3>{getTranslation("Enterprise Security", language)}</h3>
                    <p>{getTranslation("SOC 2 compliant with bank-level encryption", language)}</p>
                </div>
                <div className={styles.trustBadge}>
                    <div className={styles.badgeIcon}>⚡</div>
                    <h3>{getTranslation("99.9% Uptime", language)}</h3>
                    <p>{getTranslation("Reliable service with guaranteed availability", language)}</p>
                </div>
                <div className={styles.trustBadge}>
                    <div className={styles.badgeIcon}>💰</div>
                    <h3>{getTranslation("30-Day Money Back", language)}</h3>
                    <p>{getTranslation("Full refund if you're not completely satisfied", language)}</p>
                </div>
                <div className={styles.trustBadge}>
                    <div className={styles.badgeIcon}>🌍</div>
                    <h3>{getTranslation("Global Support", language)}</h3>
                    <p>{getTranslation("24/7 support in multiple languages", language)}</p>
                </div>
            </div>
        </div>
    );
};

const Pricing = () => {
    const navigate = useNavigate();
    const { userInfo, language } = useSelector(state => state.auth);
    const [openFAQ, setOpenFAQ] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [seatModalOpen, setSeatModalOpen] = useState(false);
    const [selectedPlanSlug, setSelectedPlanSlug] = useState(null);
    const [selectedPlanName, setSelectedPlanName] = useState('');
    const [seatCount, setSeatCount] = useState(1);
    const [minSeatCount, setMinSeatCount] = useState(1);
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    const seatBaseline = useMemo(() => Math.max(userInfo?.totalUsers || 1, 1), [userInfo?.totalUsers]);

    const hasBillingPermission = useMemo(() => {
        const categories = userInfo?.role?.categories;
        if (!Array.isArray(categories)) return false;
        return categories.some(category => {
            if (typeof category !== 'string') return false;
            const normalized = category.toLowerCase();
            return normalized === 'admin' || normalized === 'l1';
        });
    }, [userInfo?.role?.categories]);

    const mapPlanNameToEnglish = (name) => {
        const mapping = {
            "الخطة المجانية": "Freemium Plan",
            "خطة الفريق": "Team Plan",
            "خطة النمو": "Growth Plan",
            "خطة المؤسسة": "Enterprise Plan",
        };
        return mapping[name] || name;
    };

    const mapPlanNameToSlug = (name) => {
        const normalized = mapPlanNameToEnglish(name);
        switch (normalized) {
            case "Freemium Plan":
                return "freemium";
            case "Team Plan":
                return "team";
            case "Growth Plan":
                return "growth";
            case "Enterprise Plan":
                return "enterprise";
            default:
                return null;
        }
    };

    const planSeatCaps = {
        team: 100,
        growth: 500,
    };

    const initiateCheckout = async ({ planSlug, seats, closeModal = false }) => {
        try {
            setCheckoutLoading(true);
            const payload = {
                plan_slug: planSlug,
                billing_cycle: billingCycle,
                seat_count: Math.max(Math.round(seats || seatBaseline), 1),
            };
            const response = await ProjectServices.createCheckoutSession(payload);
            if (response?.status === 200 && response?.data?.url) {
                if (closeModal) {
                    setSeatModalOpen(false);
                }
                window.location.href = response.data.url;
                return;
            }
            const backendMsg = response?.data?.message || response?.data?.error;
            message.error(getTranslation(backendMsg || "Failed to create checkout session", language));
        } catch (error) {
            console.error("Error creating checkout session:", error);
            const backendMsg = error?.response?.data?.message || error?.response?.data?.error;
            if (backendMsg) {
                message.error(getTranslation(backendMsg, language));
            } else {
                message.error(getTranslation("Payment process failed", language));
            }
        } finally {
            setCheckoutLoading(false);
        }
    };

    const openSeatModal = (planSlug, planName) => {
        const baseline = seatBaseline;
        setSeatCount(baseline);
        setMinSeatCount(baseline);
        setSelectedPlanSlug(planSlug);
        setSelectedPlanName(planName);
        setSeatModalOpen(true);
    };

    const handleSeatInputChange = (value) => {
        const numeric = Number(value);
        if (!numeric || Number.isNaN(numeric)) {
            setSeatCount(minSeatCount);
            return;
        }
        const cap = planSeatCaps[selectedPlanSlug] || 500;
        const adjusted = Math.min(Math.max(Math.round(numeric), minSeatCount), cap);
        setSeatCount(adjusted);
    };

    const handleModalCancel = () => {
        if (checkoutLoading) return;
        setSeatModalOpen(false);
    };

    const handleModalOk = async () => {
        if (!selectedPlanSlug) return;
        const adjustedSeatCount = Math.max(Math.round(seatCount || 0), minSeatCount);
        setSeatCount(adjustedSeatCount);
        if (adjustedSeatCount < minSeatCount) {
            message.error(getTranslation("Seat count cannot be lower than your current active users.", language));
            return;
        }
        await initiateCheckout({ planSlug: selectedPlanSlug, seats: adjustedSeatCount, closeModal: true });
    };

    const getPlans = (basePlans, cycle) => {
        if (cycle === 'annual') {
            return basePlans.map(plan => {
                if (plan.price === '£0' || plan.price === 'مخصص' || plan.price === 'Custom') {
                    return { ...plan, period: plan.price === '£0' ? (language === 'ar' ? '/سنة' : '/year') : '', discount: null };
                }
                const monthlyPrice = parseFloat(plan.price.replace('£', ''));
                const normalizedPlanName = mapPlanNameToEnglish(plan.name);
                const discountRate = normalizedPlanName === 'Team Plan' ? 0.15 : 0.20;
                const annualPrice = Math.round(monthlyPrice * 12 * (1 - discountRate));
                return {
                    ...plan,
                    price: `£${annualPrice}`,
                    period: language === 'ar' ? '/مستخدم/سنة' : '/user/year',
                    discount: language === 'ar' ? `خصم ${Math.round(discountRate * 100)}%` : `${Math.round(discountRate * 100)}% off`
                };
            });
        }
        return basePlans.map(plan => ({ ...plan, discount: null }));
    };

    const currentPlans = getPlans(language === 'ar' ? pricingPlansArabic : pricingPlans, billingCycle);

    const handlePayment = (plan) => {
        const planSlug = mapPlanNameToSlug(plan);

        if (planSlug === "enterprise") {
            navigate("/contact");
            return;
        }

        if (!planSlug) {
            message.error(getTranslation("Invalid plan selected", language));
            return;
        }

        if (!userInfo) {
            message.info(getTranslation("Please log in to choose a plan.", language));
            navigate("/login");
            return;
        }

        if (!hasBillingPermission) {
            message.warning(getTranslation("Only Admin or L1 roles can manage billing. Please contact your organisation admin to change plans.", language));
            return;
        }

        if (!userInfo?.organisation?.id) {
            message.error(getTranslation("We couldn't detect your organisation. Please contact support.", language));
            return;
        }

        if (planSlug === "freemium") {
            initiateCheckout({ planSlug, seats: seatBaseline });
            return;
        }

        const englishName = mapPlanNameToEnglish(plan);
        openSeatModal(planSlug, englishName);
    };


    let pricingPlansWithTranslation = currentPlans;

    return (
        <div>
            <Navbar />

            <Modal
                open={seatModalOpen}
                title={getTranslation("Select seats", language)}
                okText={getTranslation("Continue to Checkout", language)}
                cancelText={getTranslation("Cancel", language)}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                confirmLoading={checkoutLoading}
                destroyOnClose
            >
                <p style={{ marginBottom: 12 }}>
                    {getTranslation("Plan selected", language)}: {getTranslation(selectedPlanName || '', language)}
                </p>
                <InputNumber
                    min={minSeatCount}
                    max={planSeatCaps[selectedPlanSlug] || 500}
                    value={seatCount}
                    onChange={handleSeatInputChange}
                    style={{ width: '100%' }}
                />
                <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                    {getTranslation("Minimum seats equals your active users count", language)}: {minSeatCount}
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                    {getTranslation("Maximum seats for this plan", language)}: {planSeatCaps[selectedPlanSlug] || 500}
                </div>
            </Modal>
            
            {/* Hero Section */}
            <div className={styles.contact__wrapper}>
                <h1>{getTranslation("Choose the right plan for your team", language)}</h1>
                <p className={styles.heroSubtitle}>
                    {getTranslation("Start with our free plan and upgrade as you grow. All plans include our core features.", language)}
                </p>
            </div>

            {/* Pricing Cards - redesigned */}
            <div className={styles.pricingOuter}>
                <div className={styles.pricingIntro}> 
                    <h2 className={styles.pricingTitle}>{getTranslation("Pricing Plans", language)}</h2>
                    <p className={styles.pricingSubtitle}>{getTranslation("Choose the plan that fits your team’s needs", language)}</p>
                    <div className={styles.billingToggle}>
                        <button 
                            className={`${styles.toggleButton} ${billingCycle === 'monthly' ? styles.active : ''}`}
                            onClick={() => setBillingCycle('monthly')}
                        >
                            {getTranslation("Monthly", language)}
                        </button>
                        <button 
                            className={`${styles.toggleButton} ${billingCycle === 'annual' ? styles.active : ''}`}
                            onClick={() => setBillingCycle('annual')}
                        >
                            {getTranslation("Annual", language)}
                        </button>
                    </div>
                </div>
                <div className={styles.plansGrid}>
                    {pricingPlansWithTranslation.map((plan, index) => {
                        return (
                            <div
                                key={index}
                                className={`${styles.planCard} ${plan.popular ? styles.planCardPopular : ''}`}
                            >
                                {plan.popular && (
                                    <span className={styles.planBadge}>{getTranslation("Popular", language)}</span>
                                )}
                                <div className={styles.planBody}>
                                    <h3 className={styles.planName}>{plan.name}</h3>
                                    
                                    {plan.description && <p className={styles.planDescription}>{plan.description}</p>}
                                    <div className={styles.planPricingRow}>
                                        <span className={styles.planPrice}>{plan.price}</span>
                                        {plan.period && <span className={styles.planPeriod}>{plan.period}</span>}
                                        {plan.discount && <span className={styles.discountBadge}>{plan.discount}</span>}
                                    </div>
                                    <ul className={styles.planFeaturesList}>
                                        {plan.features.map((feature, featureIndex) => (
                                            <li key={featureIndex} className={styles.planFeatureItem}>
                                                <span className={styles.planCheck}>✔</span>
                                                <span className={styles.planFeatureText}>{feature.value}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <button
                                    className={`${styles.planButton} ${plan.popular ? styles.planButtonPopular : ''}`}
                                    onClick={() => handlePayment(plan.name)}
                                >
                                    {plan.buttonText}
                                </button>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Feature Comparison Table */}
            <div className={styles.sectionWrapper}>
                <FeatureComparisonTable />
            </div>

            {/* Trust Indicators */}
            <div className={styles.sectionWrapper}>
                <TrustIndicators />
            </div>

            {/* Onboarding Confidence Section */}
            <div className={styles.sectionWrapper}>
                <section className={styles.onboardingSection} aria-labelledby="onboarding-title">
                    <div className={styles.onboardingHeader}>
                        <h2 id="onboarding-title" className={styles.onboardingTitle}>
                            {language === 'ar' ? onboardingMeta.title_ar : onboardingMeta.title_en}
                        </h2>
                        <p className={styles.onboardingSubtitle}>
                            {language === 'ar' ? onboardingMeta.subtitle_ar : onboardingMeta.subtitle_en}
                        </p>
                    </div>
                    <div className={styles.onboardingGrid}>
                        {onboardingSteps.map(item => (
                            <div key={item.step} className={styles.onboardingCard}>
                                <div className={styles.onboardingCardHeader}>
                                    <div className={styles.onboardingStepCircle}>{item.step}</div>
                                    <h3 className={styles.onboardingCardTitle}>
                                        {language === 'ar' ? item.title_ar : item.title_en}
                                    </h3>
                                </div>
                                <p className={styles.onboardingCardText}>
                                    {language === 'ar' ? item.text_ar : item.text_en}
                                </p>
                            </div>
                        ))}
                    </div>
                    <div className={styles.onboardingConfidenceNote}>
                        {language === 'ar' ? onboardingMeta.note_ar : onboardingMeta.note_en}
                    </div>
                </section>
            </div>

            {/* FAQ Section */}
            <div className={styles.sectionWrapper}>
                <div className={styles.faqSection}>
                    <h2 className={styles.faqTitle}>
                        {getTranslation("Frequently Asked Questions", language)}
                    </h2>
                    <div className={styles.faqContainer}>
                        {faqData.map((faq, index) => (
                            <FAQItem
                                key={index}
                                question={getTranslation(faq.question, language)}
                                answer={getTranslation(faq.answer, language)}
                                isOpen={openFAQ === index}
                                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Final CTA Section */}
            <div className={styles.ctaSection}>
                <div className={styles.ctaContent}>
                    <h2>{getTranslation("Ready to get started?", language)}</h2>
                    <p>{getTranslation("Join thousands of teams already using Compelion to manage their projects more effectively.", language)}</p>
                    <div className={styles.ctaButtons}>
                        <button 
                            className={styles.ctaPrimary}
                            onClick={() => handlePayment("Team Plan")}
                        >
                            {getTranslation("Start Free Trial", language)}
                        </button>
                        <button 
                            className={styles.ctaSecondary}
                            onClick={() => navigate("/contact")}
                        >
                            {getTranslation("Contact Sales", language)}
                        </button>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}

export default Pricing